<script setup lang="ts">
import axios from 'axios'
import { useVModel } from '@vueuse/core'
import { vMaska } from 'maska/vue'

import BaseVueTelInput from '@/components/BaseVueTelInput.vue'
import type {
  Audience,
  AudienceStore,
  CustomerCreation,
  CustomerCreationErrors,
  CustomerGender,
  MerchantAttributes
} from '@/types/types'

import type { CountryCode } from 'libphonenumber-js'
import type { Results } from 'maz-ui/components/MazPhoneNumberInput'

const emit = defineEmits<{
  (e: 'update:modelValue'): void
  (e: 'on-open-import'): void
  (e: 'search-audience', name: string): void
  (e: 'load-more-audiences', searchText: string): void
  (e: 'update:errors', errors: CustomerCreationErrors): void
}>()
const props = withDefaults(
  defineProps<{
    isCreationMode?: boolean
    alpha2Code?: CountryCode
    newCustomer: CustomerCreation
    selectedAttributes:
      | {
          [key: string]: string | null
        }
      | undefined
    gendersList: CustomerGender[]
    customerLists?: Audience[]
    attributes: MerchantAttributes[]
    errors: CustomerCreationErrors | Record<string, never>
    scopedStores?: AudienceStore[]
  }>(),
  {
    alpha2Code: 'FR',
    isCreationMode: true,
    customerLists: () => [],
    scopedStores: () => []
  }
)

const { stores, user } = useLocalStorage()
const { t } = useI18n()
const { scrollToBlockBottom } = useDOM()

const newCustomerModel = useVModel(props, 'newCustomer', emit)
const selectedAttributesModel = useVModel(props, 'selectedAttributes', emit)

const availableCountries = ref([])
const selectedAttributesList = ref([])
const selectedOptin = ref(3)
const selectedOptinEmail = ref(3)
const selectedAudiences = ref<number[]>(null)

const saveUserGender = (id: number) => {
  newCustomerModel.value.gender = props.gendersList.find((gender) => gender.id === id).id
}

const availableStores = computed(() => (props.scopedStores.length ? props.scopedStores : stores.value))

const formattedStores = computed(() => {
  const storesData = availableStores.value.map((store) => {
    return {
      id: store.id,
      text: store.name
    }
  })

  const referenceStore = newCustomerModel.value?.referenceStore

  if (referenceStore && !storesData.some((store) => store.id === referenceStore.id)) {
    storesData.push({
      id: referenceStore.id,
      text: referenceStore.name
    })
  }

  return storesData.sort((a, b) => a.text.localeCompare(b.text))
})

const optinKeyMap = ['false', 'true', 'unknown']
const formattedOptin = computed(() => {
  return [
    {
      id: 1,
      text: t('pages.customer.customerPage.blocks.create.fields.optin.false.description'),
      value: false,
      badgeIcon: 'HeroChatBubbleLeftRightIconOutline',
      badgeColor: 'red',
      badgeText: 'SMS'
    },
    {
      id: 2,
      text: t('pages.customer.customerPage.blocks.create.fields.optin.true.description'),
      value: true,
      badgeIcon: 'HeroChatBubbleLeftRightIconOutline',
      badgeColor: 'green',
      badgeText: 'SMS'
    },
    {
      id: 3,
      text: t('pages.customer.customerPage.blocks.create.fields.optin.unknown.description'),
      value: null,
      badgeIcon: 'HeroChatBubbleLeftRightIconOutline',
      badgeColor: 'gray',
      badgeText: 'SMS'
    }
  ]
})
const formattedOptinEmail = computed(() => {
  return formattedOptin.value.map((optin, index) => ({
    ...optin,
    text: t(`pages.customer.customerPage.blocks.create.fields.has_optin_email.${optinKeyMap[index]}.description`),
    badgeText: 'E-mail',
    badgeIcon: 'HeroEnvelopeIconOutline'
  }))
})

const selectedStore = computed(() => {
  return newCustomerModel.value?.referenceStore?.id || 0
})

const resetErrors = (
  field: 'referenceStore' | 'phone' | 'email' | 'loyaltyCardNumber' | 'birthday' | 'postalCode' | 'city'
) => {
  if (props.errors[field]) {
    const obj = {
      ...props.errors
    } as CustomerCreationErrors
    obj[field] = null
    emit('update:errors', obj)
  }
}

const formatBirthday = () => {
  if (newCustomerModel.value.birthday) {
    newCustomerModel.value.birthday = newCustomerModel.value.birthday.split('-').reverse().join('/')
  }
}

const handleBirthday = (date: string) => {
  resetErrors('birthday')

  newCustomerModel.value.birthdayFormatted = date.split('/').reverse().join('-')
}

const handleStoreSelection = async (id: number) => {
  resetErrors('referenceStore')

  const selectedStore = availableStores.value.find((store) => store.id === id)
  newCustomerModel.value.referenceStore = {
    id: selectedStore.id,
    name: selectedStore.name
  }

  await fetchCountries()
}

const handleOptinChoice = (id: number) => {
  newCustomerModel.value.optin = formattedOptin.value[id - 1].value
  selectedOptin.value = id
}

const handleOptinEmailChoice = (id: number) => {
  newCustomerModel.value.hasOptinEmail = formattedOptinEmail.value[id - 1].value
  selectedOptinEmail.value = id
}

const handlePhoneObject = (phoneObject: Results) => {
  resetErrors('phone')

  newCustomerModel.value.phone = phoneObject.formatNational
  newCustomerModel.value.country = phoneObject.countryCode
}

const fetchCountries = async () => {
  if (newCustomerModel.value?.referenceStore?.id) {
    const { data } = await axios.get(`stores/${newCustomerModel.value.referenceStore.id}`)
    availableCountries.value = data.data.countryStores.map((country) => {
      return country.country
    })

    newCustomerModel.value.country = props.newCustomer.country || availableCountries.value[0]?.alpha2Code
  }
}

const populateStoreSelector = () => {
  if (availableStores.value.length === 1) {
    handleStoreSelection(availableStores.value[0].id)
  }
}

const populateOptinSelector = () => {
  if (newCustomerModel.value?.optin !== null) {
    handleOptinChoice(newCustomerModel.value?.optin ? 2 : 1)
  }
  if (newCustomerModel.value?.hasOptinEmail !== null) {
    handleOptinEmailChoice(newCustomerModel.value?.hasOptinEmail ? 2 : 1)
  }
}

const formatAttributeList = (attribute: MerchantAttributes) => {
  const values = [
    {
      id: 0,
      text: t('pages.customer.customerPage.blocks.create.sections.choose')
    }
  ]
  attribute.availableValues.map((value, index) => {
    values.push({
      id: index + 1,
      text: value
    })
  })
  return values
}

const saveAttributeList = (id: number, attribute) => {
  selectedAttributesModel.value[attribute.id] = attribute.availableValues[id - 1]
  selectedAttributesList.value[attribute.id] = id
}

const populateSelectedAttributesList = () => {
  if (!selectedAttributesModel.value) {
    return
  }
  Object.entries(selectedAttributesModel.value).map(([key, value]) => {
    const selectedInput = props.attributes.find((attribute) => attribute.id === key)
    const isNotAList = !selectedInput.availableValues
    if (isNotAList) {
      return
    }
    const findIndexOfValue = selectedInput.availableValues.indexOf(value) + 1

    selectedAttributesList.value[key] = findIndexOfValue
  })
}

onMounted(() => {
  fetchCountries()
  populateStoreSelector()
  populateOptinSelector()
  populateSelectedAttributesList()
  formatBirthday()

  selectedAudiences.value = newCustomerModel.value.lists.filter((list) => list.type === 'static').map((list) => list.id)
})

watch(selectedAudiences, (newVal) => {
  newCustomerModel.value.lists = props.customerLists.filter(
    (list) => newVal.includes(list.id) && list.type === 'static'
  )
})
</script>

<template>
  <div class="wrap-customer-modal space-y-6 text-left">
    <div class="space-y-5">
      <div>
        <p class="block text-sm font-semibold text-foreground">
          {{ t('pages.customer.customerPage.blocks.create.sections.customerInfo.title') }}
        </p>
        <span class="text-sm text-muted-foreground">
          {{ t('pages.customer.customerPage.blocks.create.sections.customerInfo.subtitle') }}</span
        >
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-foreground">
          {{ t('pages.customer.customerPage.blocks.create.fields.store') }}<sup class="ml-1 text-red-600">*</sup>
        </label>
        <ComboboxSelect
          value-key="id"
          label-key="text"
          :placeholder="t('pages.customer.customerPage.blocks.create.fields.chooseStore')"
          :model-value="selectedStore"
          :options="formattedStores"
          trigger-class="w-full"
          @update:model-value="handleStoreSelection"
        />
      </div>
      <p v-if="errors.referenceStore" class="mt-1! text-sm text-red-600">
        {{ errors.referenceStore }}
      </p>

      <BaseVueTelInput
        v-model="newCustomerModel.phone"
        :country-code="alpha2Code"
        :model-value:errors="errors.phone"
        :label="t('pages.customer.customerPage.blocks.create.fields.phone')"
        :errors="errors.phone"
        :countries="
          availableCountries.map((x) => x.alpha2Code).length ? availableCountries.map((x) => x.alpha2Code) : ['FR']
        "
        @update:phone-object="handlePhoneObject"
      />

      <AppFormInput
        id="email"
        v-model="newCustomerModel.email"
        fullwidth
        :label="t('pages.customer.customerPage.blocks.create.fields.email')"
        type="email"
        name="email"
        :errors="errors.email ? [errors.email] : []"
        @update:model-value="resetErrors('email')"
      />

      <AppFormInput
        id="loyaltyCardNumber"
        v-model="newCustomerModel.loyaltyCardNumber"
        fullwidth
        :label="t('pages.customer.customerPage.blocks.create.fields.loyaltyCardNumber')"
        type="text"
        name="loyaltyCardNumber"
        :errors="errors.loyaltyCardNumber ? [errors.loyaltyCardNumber] : []"
        @update:model-value="resetErrors('loyaltyCardNumber')"
      />

      <div class="flex w-full flex-col gap-2">
        <div class="flex w-full items-start gap-5">
          <div class="w-full">
            <label for="hasOptin" class="mb-1 block text-sm font-medium text-foreground">
              {{ t('pages.customer.customerPage.blocks.create.fields.optin.label') }}
            </label>
            <ComboboxSelect
              id="hasOptin"
              v-model="newCustomerModel.optin"
              value-key="id"
              label-key="text"
              :options="formattedOptin"
              :model-value="selectedOptin"
              trigger-class="w-full"
              @update:model-value="handleOptinChoice"
            >
              <template #trigger="{ selected }">
                <div v-if="selected" class="flex min-w-0 flex-1 items-center gap-2">
                  <BaseChip
                    :color="(selected as any).badgeColor"
                    :icon="(selected as any).badgeIcon"
                    :text="(selected as any).badgeText"
                  />
                  <span class="truncate">{{ selected.label }}</span>
                </div>
              </template>
              <template #option="{ option, selected }">
                <div class="flex items-center gap-2">
                  <BaseChip
                    :color="(option as any).badgeColor"
                    :icon="(option as any).badgeIcon"
                    :text="(option as any).badgeText"
                  />
                  <span :class="selected ? 'font-semibold text-primary' : ''">
                    {{ option.label }}
                  </span>
                </div>
              </template>
            </ComboboxSelect>
          </div>
          <div class="w-full">
            <label for="hasOptinEmail" class="mb-1 block text-sm font-medium text-foreground">
              {{ t('pages.customer.customerPage.blocks.create.fields.has_optin_email.label') }}
            </label>
            <ComboboxSelect
              id="hasOptinEmail"
              v-model="newCustomerModel.hasOptinEmail"
              value-key="id"
              label-key="text"
              :options="formattedOptinEmail"
              :model-value="selectedOptinEmail"
              trigger-class="w-full"
              @update:model-value="handleOptinEmailChoice"
            >
              <template #trigger="{ selected }">
                <div v-if="selected" class="flex min-w-0 flex-1 items-center gap-2">
                  <BaseChip
                    :color="(selected as any).badgeColor"
                    :icon="(selected as any).badgeIcon"
                    :text="(selected as any).badgeText"
                  />
                  <span class="truncate">{{ selected.label }}</span>
                </div>
              </template>
              <template #option="{ option, selected }">
                <div class="flex items-center gap-2">
                  <BaseChip
                    :color="(option as any).badgeColor"
                    :icon="(option as any).badgeIcon"
                    :text="(option as any).badgeText"
                  />
                  <span :class="selected ? 'font-semibold text-primary' : ''">
                    {{ option.label }}
                  </span>
                </div>
              </template>
            </ComboboxSelect>
          </div>
        </div>
        <p v-if="newCustomerModel.optin || newCustomerModel.hasOptinEmail" class="text-sm text-muted-foreground">
          {{
            t('pages.customer.customerPage.blocks.create.fields.optinDescription', {
              merchantName: user.merchant.name,
              sms: !newCustomerModel.hasOptinEmail && newCustomerModel.optin ? 'SMS' : '',
              email: newCustomerModel.hasOptinEmail && !newCustomerModel.optin ? 'E-mail' : '',
              smsAndEmail:
                newCustomerModel.optin && newCustomerModel.hasOptinEmail
                  ? t('pages.customer.customerPage.blocks.create.fields.optinDescriptionSmsEmail')
                  : ''
            })
          }}
        </p>
      </div>

      <div class="flex w-full items-center gap-5">
        <AppFormInput
          id="firstname"
          v-model="newCustomerModel.firstname"
          fullwidth
          :label="t('pages.customer.customerPage.blocks.create.fields.firstName')"
          type="firstname"
          name="firstname"
          maxlength="50"
          class="w-full"
        />
        <AppFormInput
          id="lastname"
          v-model="newCustomerModel.lastname"
          fullwidth
          :label="t('pages.customer.customerPage.blocks.create.fields.lastName')"
          type="lastname"
          name="lastname"
          maxlength="50"
          class="w-full"
        />
      </div>

      <div>
        <label for="gender" class="mb-1 block text-sm font-medium text-foreground">
          {{ t('pages.customer.customerPage.blocks.create.fields.genders.label') }}
        </label>
        <ComboboxSelect
          id="gender"
          value-key="id"
          label-key="text"
          fullwidth
          placeholder=""
          :model-value="newCustomerModel.gender"
          :options="gendersList"
          trigger-class="w-full"
          @update:model-value="saveUserGender"
        />
      </div>

      <div>
        <label for="birthday" class="mb-1.5 flex w-full text-left text-sm font-normal text-foreground">
          {{ t('pages.customer.customerPage.blocks.create.fields.birthday') }}
        </label>
        <Input
          id="birthday"
          v-model="newCustomerModel.birthday"
          v-maska="'##/##/####'"
          name="birthday"
          placeholder="DD/MM/YYYY"
          @update:model-value="handleBirthday"
        />
        <p v-if="errors.birthday" class="mt-2 text-sm text-red-600">
          {{ errors.birthday }}
        </p>
      </div>

      <div class="flex items-center gap-5">
        <AppFormInput
          id="postalCode"
          v-model="newCustomerModel.postalCode"
          fullwidth
          :label="t('pages.customer.customerPage.blocks.create.fields.postalCode')"
          type="postalCode"
          name="postalCode"
          class="w-full"
          :errors="errors.postalCode ? [errors.postalCode] : []"
        />
        <AppFormInput
          id="city"
          v-model="newCustomerModel.city"
          fullwidth
          :label="t('pages.customer.customerPage.blocks.create.fields.city')"
          type="city"
          name="city"
          class="w-full"
          :errors="errors.city ? [errors.city] : []"
        />
      </div>

      <template v-for="attribute in attributes" :key="attribute.id">
        <AppFormInput
          v-if="attribute.type === 'float'"
          :id="attribute.id"
          v-model="selectedAttributesModel[attribute.id]"
          fullwidth
          :label="attribute.label"
          type="number"
          :name="attribute.label"
        />
        <div v-else-if="attribute.type === 'list'">
          <label class="mb-1 block text-sm font-medium text-foreground">
            {{ attribute.label }}
          </label>
          <ComboboxSelect
            value-key="id"
            label-key="text"
            :placeholder="t('pages.customer.customerPage.blocks.create.sections.choose')"
            :model-value="selectedAttributesList[attribute.id]"
            :options="formatAttributeList(attribute)"
            trigger-class="w-full"
            @update:model-value="saveAttributeList($event, attribute)"
          />
        </div>
        <AppFormInput
          v-else-if="attribute.type === 'date'"
          :id="attribute.id"
          v-model="selectedAttributesModel[attribute.id]"
          fullwidth
          :label="attribute.label"
          type="date"
          :name="attribute.label"
        />
      </template>
    </div>

    <div ref="segmentsContainer">
      <BaseDisclosure
        is-default-open
        :has-border="false"
        title-paddings="!p-0"
        @click="scrollToBlockBottom($refs.segmentsContainer as HTMLElement)"
      >
        <template #title>
          <div class="flex flex-col">
            <div class="flex gap-1">
              <p class="text-sm font-semibold text-foreground">
                {{ t('pages.customer.customerPage.blocks.create.sections.segmentation.title') }}
              </p>
              <span class="font-normal italic">
                {{ t('pages.customer.customerPage.blocks.create.sections.optional') }}</span
              >
            </div>
            <span class="block text-sm text-muted-foreground">{{
              t('pages.customer.customerPage.blocks.create.sections.segmentation.subtitle')
            }}</span>
          </div>
        </template>

        <div class="space-y-6">
          <div v-if="customerLists">
            <label for="description" class="mb-2 block text-sm font-medium text-foreground"
              >{{ t('pages.customer.customerPage.blocks.create.sections.segmentation.segment') }}
            </label>
            <div class="flex flex-wrap gap-5">
              <MultiSelectAudiences
                v-if="selectedAudiences !== null"
                v-model="selectedAudiences"
                :audiences="customerLists"
                :default-filter-placeholder="t('global.multiselect.audiences.listsSearchPlaceholder')"
                :default-empty-filter-message="t('global.multiselect.audiences.noListsSearch')"
                :default-empty-message="t('global.multiselect.audiences.noLists')"
                hide-empty-categories
              />
            </div>
          </div>
        </div>
      </BaseDisclosure>
    </div>
  </div>
</template>

<style>
.wrap-customer-modal label {
  font-weight: 400;
}
</style>
